import React, { useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Box, Typography, Avatar, Menu, MenuItem, Badge, IconButton } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import KitchenIcon from '@mui/icons-material/Kitchen';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HistoryIcon from '@mui/icons-material/History';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  useAuth,
  useHeaderNotifications,
  usePermissions,
  ProtectedRoute,
  PermissionProvider,
  PermissionGate,
  AppSidebar,
  MaintenanceGuard,
  DemoBanner,
  headerRouteTitle,
} from '@resort-os/ui';
import { Header, AppSwitcher } from '@resortifysolutions/layout';
import type { AppSwitcherApp } from '@resortifysolutions/layout';
import {
  Home,
  Hotel,
  Store,
  BarChart,
  Package,
  CalendarDays,
  Heart,
  Send,
  Users,
  ShieldCheck,
  Truck,
  Layers,
  Globe,
} from '@resortifysolutions/icons';
import { getAllAppRoutes } from '@resort-os/config';
import { bffNotificationService } from '@resort-os/api-client';
import OrdersPage from './pages/OrdersPage';
import SplitDisplay from './pages/SplitDisplay';
import HistoryPage from './pages/HistoryPage';
import TablesPage from './pages/TablesPage';
import CalendarPage from './pages/CalendarPage';
import TourWrapper from './components/TourWrapper';

const ACCENT = '#2C3E2D';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  subtitle?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Products',
    path: '/products',
    icon: <PointOfSaleIcon sx={{ fontSize: 20 }} />,
    subtitle: 'Menu items',
  },
  {
    label: 'Orders',
    path: '/orders',
    icon: <KitchenIcon sx={{ fontSize: 20 }} />,
    subtitle: 'Active orders',
  },
  {
    label: 'Tables',
    path: '/tables',
    icon: <RestaurantIcon sx={{ fontSize: 20 }} />,
    subtitle: 'Floor plan',
  },
  {
    label: 'History',
    path: '/history',
    icon: <HistoryIcon sx={{ fontSize: 20 }} />,
    subtitle: 'Past transactions',
  },
  {
    label: 'Calendar',
    path: '/calendar',
    icon: <CalendarMonthIcon sx={{ fontSize: 20 }} />,
    subtitle: 'Daily overview',
  },
];

function PageHeader({ title }: { title: string }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          color: '#6B6560',
          letterSpacing: '0.1em',
          mb: 0.5,
        }}
      >
        Point of Sale
      </Typography>
      <Typography
        variant="h1"
        sx={{
          fontSize: '1.75rem',
          fontWeight: 600,
          color: '#1A1A1A',
        }}
      >
        {title}
      </Typography>
      <Box sx={{ width: 32, height: 2, bgcolor: ACCENT, mt: 1.5 }} />
    </Box>
  );
}

function AppShell() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const notifications = useHeaderNotifications();
  const authAppUrl = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
  const { apps: allowedApps } = usePermissions();
  const appRoutes = getAllAppRoutes();
  const [appAnchorEl, setAppAnchorEl] = useState<HTMLElement | null>(null);

  const APP_ICONS: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
    home: { icon: <Home size={20} />, color: '#2C3E2D', bgColor: '#E8F5E9' },
    reception: { icon: <Hotel size={20} />, color: '#1565C0', bgColor: '#E3F2FD' },
    pos: { icon: <Store size={20} />, color: '#2E7D32', bgColor: '#E8F5E9' },
    accountant: { icon: <BarChart size={20} />, color: '#6A1B9A', bgColor: '#F3E5F5' },
    inventory: { icon: <Package size={20} />, color: '#BF360C', bgColor: '#FBE9E7' },
    spa: { icon: <Heart size={20} />, color: '#00695C', bgColor: '#E0F2F1' },
    marketing: { icon: <Send size={20} />, color: '#E65100', bgColor: '#FFF3E0' },
    groupEvents: { icon: <CalendarDays size={20} />, color: '#00838F', bgColor: '#E0F7FA' },
    hr: { icon: <Users size={20} />, color: '#4E342E', bgColor: '#EFEBE9' },
    reports: { icon: <BarChart size={20} />, color: '#1565C0', bgColor: '#E3F2FD' },
    africaVerify: { icon: <ShieldCheck size={20} />, color: '#0D6458', bgColor: '#E0F2F1' },
    odoCars: { icon: <Truck size={20} />, color: '#E65100', bgColor: '#FFF3E0' },
    communicationHub: { icon: <Layers size={20} />, color: '#4A148C', bgColor: '#F3E5F5' },
    tourAgents: { icon: <Globe size={20} />, color: '#004D40', bgColor: '#E0F2F1' },
  };

  const apps: AppSwitcherApp[] = appRoutes.map(route => {
    const entry = APP_ICONS[route.id] || {
      icon: <Home size={20} />,
      color: '#2C3E2D',
      bgColor: '#E8F5E9',
    };
    return {
      id: route.id,
      name: route.name,
      icon: entry.icon,
      color: entry.color,
      bgColor: entry.bgColor,
      href: route.disabled ? undefined : route.baseUrl,
      disabled: route.disabled,
    };
  });

  function getCurrentAppId(): string {
    if (typeof window === 'undefined') return '';
    const { hostname, port } = window.location;
    const current = port ? `${hostname}:${port}` : hostname;
    const map: Record<string, string> = {
      [import.meta.env.VITE_APP_POS_KEY || 'localhost:5002']: 'pos',
    };
    return map[current] ?? '';
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAF8' }}>
      <Header
        title={headerRouteTitle(location.pathname, NAV_ITEMS)}
        badge="R"
        onMenuClick={() => {
          const trigger = document.querySelector(
            'header [data-apps-trigger], header [aria-label="Apps"], header [aria-label="Toggle menu"]',
          ) as HTMLElement;
          if (trigger) setAppAnchorEl(trigger);
        }}
        unreadCount={notifications.unreadCount}
        onNotificationsClick={notifications.onNotificationsClick}
        onSettingsClick={() => {
          window.location.href = `${authAppUrl}/settings`;
        }}
        userMenu={
          user
            ? {
                name:
                  `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User',
                role: user.role || '',
                onLogout: () => {
                  void logout();
                },
                onProfile: () => {
                  window.location.href = `${authAppUrl}/profile`;
                },
                onSettings: () => {
                  window.location.href = `${authAppUrl}/settings`;
                },
              }
            : undefined
        }
      />
      {notifications.notificationModal}
      <AppSwitcher
        apps={apps}
        currentAppId={getCurrentAppId()}
        anchorEl={appAnchorEl}
        onClose={() => setAppAnchorEl(null)}
        allowedApps={allowedApps}
      />
      <AppSidebar appTitle="Point of Sale" navItems={NAV_ITEMS} activePath={location.pathname} />
      <Box sx={{ pt: '64px', pl: '192px' }}>
        <Box component="main" sx={{ p: 4, maxWidth: 1400 }}>
          <Routes>
            <Route path="/" element={<Navigate to="/products" replace />} />
            <Route path="/orders" element={<SplitDisplay PageHeader={PageHeader} />} />
            <Route path="/products" element={<OrdersPage PageHeader={PageHeader} />} />
            <Route path="/tables" element={<TablesPage PageHeader={PageHeader} />} />
            <Route path="/history" element={<HistoryPage PageHeader={PageHeader} />} />
            <Route path="/calendar" element={<CalendarPage PageHeader={PageHeader} />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  const { user } = useAuth();

  return (
    <MaintenanceGuard>
      <PermissionGate>
        <ProtectedRoute>
          <PermissionProvider role={user?.role || ''}>
            <AppShell />
            <TourWrapper />
            <DemoBanner />
          </PermissionProvider>
        </ProtectedRoute>
      </PermissionGate>
    </MaintenanceGuard>
  );
}

export default App;
